import React, { useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import { Card, Input } from 'antd';
import { Button } from '../../../components/button';
import { useUnsavedChanges } from '../../../contexts/unsaved-changes';
import { MinimalAppConfig } from '../../../types';
import style from './style.module.scss';

const { Meta } = Card;

interface Props {
    isLoading: boolean;
    isUpdating: boolean;
    minimalAppConfig: MinimalAppConfig | null;
    onSubmit: (conf: MinimalAppConfig) => void;
}

export const MinimalAppCard = (props: Props) => {
    const valuesSnapshot = React.useRef<null | MinimalAppConfig>(null);
    const formik = useFormik<MinimalAppConfig>({
        initialValues: {
            miniOSVersion: '',
            minAndroidBuildVersion: 0,
            minAndroidVersionName: '',
        },
        onSubmit: (values) => {
            if (valuesSnapshot.current) {
                props.onSubmit(values);
            }
        },
    });

    const handleSaveFromModal = useCallback(async () => {
        await formik.submitForm();
    }, [formik]);

    useUnsavedChanges({ dirty: formik.dirty, onSave: handleSaveFromModal });

    useEffect(() => {
        if (props.minimalAppConfig) {
            valuesSnapshot.current = props.minimalAppConfig;
            formik.resetForm({ values: props.minimalAppConfig });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.minimalAppConfig]);

    const hasChanged = () => {
        return (
            formik.values.miniOSVersion !==
                valuesSnapshot.current?.miniOSVersion ||
            formik.values.minAndroidBuildVersion !==
                valuesSnapshot.current.minAndroidBuildVersion ||
            formik.values.minAndroidVersionName !==
                valuesSnapshot.current.minAndroidVersionName
        );
    };

    return (
        <Card bordered={false} loading={props.isLoading} className={style.card}>
            <Meta title="Minimal app configuration" />
            <form className={style.card_content} onSubmit={formik.handleSubmit}>
                <div>
                    Minimal iOS version:{' '}
                    <Input
                        type="text"
                        disabled={props.isUpdating}
                        name="miniOSVersion"
                        value={formik.values.miniOSVersion}
                        onChange={formik.handleChange}
                    />
                </div>
                <div>
                    Minimal android version (legacy build number):{' '}
                    <Input
                        disabled={props.isUpdating}
                        type="number"
                        name="minAndroidBuildVersion"
                        value={formik.values.minAndroidBuildVersion}
                        onChange={formik.handleChange}
                    />
                </div>
                <div>
                    Minimal android version name:{' '}
                    <Input
                        type="text"
                        disabled={props.isUpdating}
                        name="minAndroidVersionName"
                        value={formik.values.minAndroidVersionName ?? ''}
                        onChange={formik.handleChange}
                    />
                </div>
                <Button
                    variant="primary"
                    disabled={!hasChanged()}
                    loading={props.isUpdating}
                    onClick={() => formik.submitForm()}
                >
                    Save
                </Button>
            </form>
        </Card>
    );
};
